import { Box, Button, Grid, Typography } from '@mui/material'
import ListAltIcon from '@mui/icons-material/ListAlt'
import Fields from '../Common/Fields'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../redux/hook'
import {
    SaveProgramAPIPayload,
    SaveTemplates,
    SelectedTemplatesData,
} from './interfaces'
import { saveProgram } from '../../services/programServices'
import {
    setProgramData,
    updateProgramField,
    updateProgramFields,
    updateProgramTemplate,
    updateProgramTemplates,
    updateProgramTemplatesWithSubTemplates,
} from '../../redux/slices/programSlice'
import TemplateHierarchy from './TemplateHierarchy'
import { diffOrgSelection, validateProgramInstance } from './helperFunctions'
import {
    ORG_FIELD_IDS,
    orgFieldLabelKeys,
    pickerTitleKeys,
    ProgramAttributes,
    type OrgFieldAttribute,
} from './constants'
import { useTranslation } from 'react-i18next'
import { IListItemValue } from '../../interfaces/Interfaces'
import OrgPickerModal from './OrgPickerModal'
import OrgCompactField from './OrgCompactField'
import theme from '../../theme/theme'

interface ProgramInstanceProps {
    instanceId: string | null
    onClose: (refresh: boolean) => void
    readOnly: boolean
}

const ProgramInstance: React.FC<ProgramInstanceProps> = (
    props: ProgramInstanceProps,
) => {
    const { t } = useTranslation()
    const { instanceId, onClose, readOnly } = props
    const programFields = useAppSelector((state) => state.program.fields)
    const programTypeListData = useAppSelector(
        (state) => state.program.ProgramTypeListData,
    )
    const programTemplates = useAppSelector(
        (state) => state.program.templatesData,
    )

    const [pickerField, setPickerField] = useState<OrgFieldAttribute | null>(
        null,
    )
    const [hasChanges, setHasChanges] = useState(false)

    // Per-field baseline captured lazily the first time a picker opens — at that moment the
    // field's value is the server-loaded value (the only way to mutate it is via the modal).
    const initialOrgValuesRef = useRef<
        Partial<Record<OrgFieldAttribute, IListItemValue[]>>
    >({})
    const containerRef = useRef<HTMLDivElement>(null)

    // Reset captured baselines whenever a different program is loaded
    useEffect(() => {
        initialOrgValuesRef.current = {}
        setHasChanges(false)
    }, [instanceId])

    // Derived (not separately-tracked state) so it can never go stale: it recomputes from the
    // live field value every time `programFields` changes, whether that change came from
    // applying a picker or from an unrelated cascade (e.g. changing Owner clears that org out of
    // Carrier/Shipper/Receiver/Customer via the updateProgramField reducer) — either way the
    // badge always reflects what's actually about to be saved.
    const orgDiffs = useMemo(() => {
        const diffs: Partial<
            Record<OrgFieldAttribute, { added: number; removed: number }>
        > = {}
        for (const id of ORG_FIELD_IDS) {
            const baseline = initialOrgValuesRef.current[id]
            if (!baseline) continue
            const current = programFields?.find((f) => f.id === id)?.value || []
            const { addedOrgs, removedOrgs } = diffOrgSelection(
                baseline,
                current,
            )
            if (addedOrgs.length || removedOrgs.length) {
                diffs[id] = {
                    added: addedOrgs.length,
                    removed: removedOrgs.length,
                }
            }
        }
        return diffs
    }, [programFields])

    const openPicker = (id: OrgFieldAttribute) => {
        if (!(id in initialOrgValuesRef.current)) {
            const current = programFields?.find((f) => f.id === id)?.value
            initialOrgValuesRef.current[id] = Array.isArray(current)
                ? [...current]
                : []
        }
        setPickerField(id)
    }

    const ownerOrgId = useMemo(
        () =>
            programFields?.find((i) => i.id === ProgramAttributes.OWNER)?.value
                ?.id || '',
        [programFields],
    )
    const programTypeId = useMemo(
        () =>
            programFields?.find((i) => i.id === ProgramAttributes.PROGRAMTYPE)
                ?.value || '',
        [programFields],
    )
    const nonOrgFields = useMemo(
        () =>
            programFields?.filter(
                (f) => !ORG_FIELD_IDS.includes(f.id as OrgFieldAttribute),
            ) ?? [],
        [programFields],
    )

    const getOrgField = (id: OrgFieldAttribute) =>
        programFields?.find((f) => f.id === id)

    const renderOrgField = (id: OrgFieldAttribute) => (
        <OrgCompactField
            key={id}
            fieldId={id}
            label={t(orgFieldLabelKeys[id])}
            manageLabel={t(pickerTitleKeys[id])}
            value={getOrgField(id)?.value || []}
            onExpand={() => openPicker(id)}
            disabled={readOnly}
            added={orgDiffs[id]?.added}
            removed={orgDiffs[id]?.removed}
        />
    )

    const dispatch = useAppDispatch()

    const titleText = useMemo(() => {
        if (instanceId) {
            return readOnly ? t('viewProgram') : t('editProgram')
        }
        return t('addProgram')
    }, [instanceId, readOnly, t])

    useEffect(() => {
        dispatch(setProgramData(instanceId))
    }, [instanceId, dispatch])

    const onChangeFields = (id: string, value: any) => {
        setHasChanges(true)
        dispatch(updateProgramField({ id, value }))
        if (id === ProgramAttributes.PROGRAMTYPE) {
            const ownerOrgId =
                programFields?.find((i) => i.id === ProgramAttributes.OWNER)
                    ?.value?.id || ''
            dispatch(
                updateProgramTemplatesWithSubTemplates({
                    orgId: ownerOrgId,
                    programTypeId: value,
                    programTypeListData,
                    refresh: true,
                }),
            )
        } else if (id === ProgramAttributes.OWNER) {
            const programTypeId =
                programFields?.find(
                    (i) => i.id === ProgramAttributes.PROGRAMTYPE,
                )?.value || ''
            dispatch(
                updateProgramTemplatesWithSubTemplates({
                    orgId: value?.id,
                    programTypeId,
                    programTypeListData,
                    refresh: true,
                }),
            )
        }
    }

    const onSelectProgramTemplate = (
        categoryId: string,
        id: string,
        templateId: string,
        value: boolean,
    ) => {
        setHasChanges(true)
        dispatch(updateProgramTemplate({ categoryId, id, templateId, value }))
    }

    const handleCloseFields = (refresh: boolean = false) => {
        onClose(refresh)
        dispatch(updateProgramFields([]))
        dispatch(updateProgramTemplates([]))
    }

    const onSaveFields = async () => {
        const validateResponse = validateProgramInstance(
            programFields,
            programTemplates,
        )
        if (validateResponse?.success) {
            const payload: SaveProgramAPIPayload = {
                name: '',
                programTypeId: '',
                ownerOrganization: ' ',
                carriers: [],
                receivers: [],
                customers: [],
                subscriberOrganizations: [],
                templates: [],
            }
            programFields?.forEach((item) => {
                if (item.id === ProgramAttributes.NAME) {
                    payload.name = item.value
                } else if (item.id === ProgramAttributes.PROGRAMTYPE) {
                    payload.programTypeId = item.value
                } else if (item.id === ProgramAttributes.OWNER) {
                    payload.ownerOrganization = item.value?.id
                } else if (item.id === ProgramAttributes.SUBSCRIBER) {
                    payload.subscriberOrganizations = item.value?.map(
                        (i: IListItemValue) => i.id,
                    )
                } else if (item.id === ProgramAttributes.CARRIER) {
                    payload.carriers = item.value?.map(
                        (i: IListItemValue) => i.id,
                    )
                } else if (item.id === ProgramAttributes.RECEIVER) {
                    payload.receivers = item.value?.map(
                        (i: IListItemValue) => i.id,
                    )
                } else if (item.id === ProgramAttributes.CUSTOMER) {
                    payload.customers = item.value?.map(
                        (i: IListItemValue) => i.id,
                    )
                }
            })
            const templatesData: SaveTemplates[] = []
            programTemplates.forEach((item) => {
                const selectedTemplatesData: SelectedTemplatesData[] = []
                item.categoryTemplates?.forEach((i) => {
                    selectedTemplatesData.push({
                        categoryId: i.name,
                        selectedTemplates: i.selectedTemplates || [],
                    })
                })
                templatesData.push({
                    categoryId: item.categoryId,
                    templates: selectedTemplatesData,
                })
            })
            payload.templates = templatesData
            const result = await saveProgram(instanceId, payload)
            if (result?.success) {
                handleCloseFields(true)
            }
        } else {
            dispatch(updateProgramFields(validateResponse?.programFields || []))
            dispatch(
                updateProgramTemplates(
                    validateResponse?.programTemplates || [],
                ),
            )
        }
    }

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Escape') {
            handleCloseFields(false)
        }
    }

    useEffect(() => {
        // This view replaces the Programs list outright (see Programs/index.tsx) rather than
        // overlaying it, so there's no background content to trap focus away from — but it still
        // needs the two things a screen-reader/keyboard user would otherwise get from a real
        // Dialog: an announced landmark on mount, and Escape to go back to the list.
        containerRef.current?.focus()
    }, [])

    return (
        <Grid
            ref={containerRef}
            role="region"
            aria-labelledby="program-instance-heading"
            tabIndex={-1}
            onKeyDown={handleKeyDown}
            sx={{
                margin: '30px',
                border: `1px solid ${theme.palette.primary.main}`,
                borderRadius: '20px',
                maxHeight: 'calc(100vh - 199px)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                '&:focus': { outline: 'none' },
            }}
        >
            {/* Page header */}
            <Grid
                container
                sx={{
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                    px: 3,
                    pt: 2,
                    pb: 1.5,
                    flexShrink: 0,
                    borderBottom: `1px solid ${theme.palette.primary.main}33`,
                }}
            >
                <Box>
                    <Typography
                        id="program-instance-heading"
                        variant="h4"
                        component="h1"
                    >
                        {titleText}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1.5,
                        alignItems: 'center',
                        pb: 0.5,
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={() => handleCloseFields(false)}
                        sx={{
                            fontSize: 12,
                            textTransform: 'none',
                            borderRadius: '4px',
                            px: 2.5,
                        }}
                        id="discard-changes-button"
                    >
                        {t('cancel')}
                    </Button>
                    {!readOnly && (
                        <Button
                            variant="contained"
                            onClick={onSaveFields}
                            disabled={!hasChanges}
                            sx={{
                                fontSize: 12,
                                textTransform: 'none',
                                borderRadius: '4px',
                                px: 2.5,
                            }}
                            id="save-program-button"
                        >
                            {t(instanceId ? 'updateProgram' : 'saveProgram')}
                        </Button>
                    )}
                </Box>
            </Grid>

            {/* Body */}
            <Box
                sx={{
                    flex: 1,
                    overflow: 'hidden',
                    p: 2,
                    display: 'flex',
                    gap: 2,
                }}
            >
                {/* Left card */}
                <Box
                    sx={{
                        flex: '0 0 calc(62% - 8px)',
                        bgcolor: '#fff',
                        border: '1px solid #E0E0E0',
                        borderRadius: '8px',
                        p: 2.5,
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 2,
                        }}
                    >
                        <ListAltIcon
                            sx={{ fontSize: 18, color: 'primary.main' }}
                        />
                        <Typography
                            sx={{
                                fontSize: 16,
                                fontWeight: 600,
                                color: 'primary.main',
                            }}
                        >
                            {t('programDetails')}
                        </Typography>
                    </Box>
                    <Fields
                        fields={nonOrgFields}
                        onChangeFields={onChangeFields}
                        disabled={readOnly}
                    />
                    <Box sx={{ display: 'flex', gap: 2, mt: 1, mb: 2 }}>
                        {ORG_FIELD_IDS.slice(0, 2).map(renderOrgField)}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {ORG_FIELD_IDS.slice(2, 4).map(renderOrgField)}
                    </Box>
                </Box>

                {/* Right card — Entity Templates */}
                <Box
                    sx={{
                        flex: '0 0 calc(38% - 8px)',
                        bgcolor: '#fff',
                        border: '1px solid #E0E0E0',
                        borderRadius: '8px',
                        p: 2.5,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 2,
                            flexShrink: 0,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: 16,
                                fontWeight: 600,
                                color: 'primary.main',
                            }}
                        >
                            {t('entityTemplates')}
                        </Typography>
                    </Box>
                    <Box sx={{ flex: 1, overflowY: 'auto' }}>
                        {programTemplates?.map((category) => (
                            <Box key={category.categoryId}>
                                {category?.categoryTemplates?.length >= 1 ||
                                category?.categoryDefaultAndDisabledTemplates
                                    ?.length >= 1 ? (
                                    <Box sx={{ marginBottom: '12px' }}>
                                        <Typography
                                            sx={{
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                color: '#000',
                                            }}
                                        >
                                            {category.categoryName}
                                        </Typography>
                                    </Box>
                                ) : null}
                                {category?.categoryDefaultAndDisabledTemplates?.map(
                                    (item) => (
                                        <Box
                                            key={category.categoryId + item.id}
                                            sx={{
                                                marginBottom: '10px',
                                                paddingLeft: '16px',
                                            }}
                                        >
                                            <TemplateHierarchy
                                                templates={item.templates}
                                                selectedTemplates={
                                                    item.selectedTemplates || []
                                                }
                                                key={programTypeId + ownerOrgId}
                                                onSelect={(
                                                    id: string,
                                                    value: boolean,
                                                ) =>
                                                    onSelectProgramTemplate(
                                                        category.categoryId,
                                                        item.id,
                                                        id,
                                                        value,
                                                    )
                                                }
                                                disabled={true}
                                            />
                                        </Box>
                                    ),
                                )}
                                {category?.categoryTemplates?.map((item) => (
                                    <Box
                                        key={category.categoryId + item.id}
                                        sx={{
                                            marginBottom: '10px',
                                            paddingLeft: '16px',
                                        }}
                                    >
                                        <TemplateHierarchy
                                            templates={item.templates}
                                            selectedTemplates={
                                                item.selectedTemplates || []
                                            }
                                            key={programTypeId + ownerOrgId}
                                            onSelect={(
                                                id: string,
                                                value: boolean,
                                            ) =>
                                                onSelectProgramTemplate(
                                                    category.categoryId,
                                                    item.id,
                                                    id,
                                                    value,
                                                )
                                            }
                                            disabled={readOnly}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>

            {pickerField && (
                <OrgPickerModal
                    open={true}
                    title={t(pickerTitleKeys[pickerField])}
                    value={getOrgField(pickerField)?.value || []}
                    savedValue={initialOrgValuesRef.current[pickerField] ?? []}
                    excludeOrgs={getOrgField(pickerField)?.excludeOrgs}
                    onApply={(selected) => {
                        // orgDiffs is derived from programFields (see above), so it recomputes
                        // automatically once this dispatch lands — no manual sync needed.
                        onChangeFields(pickerField, selected)
                        setPickerField(null)
                    }}
                    onCancel={() => setPickerField(null)}
                />
            )}
        </Grid>
    )
}

export default ProgramInstance
